"use client";

import Image from "next/image";
import type { Job } from "@/lib/api/types";
import { formatShortDate } from "@/lib/date/format-short-date";
import type { JobAtsLinkSummary } from "@/lib/api/model";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { JobActionsMenu } from "@/features/jobs/components/job-actions-menu";
import { Button } from "@/components/ui/button";
import { LinkButton } from "@/components/ui/link-button";
import { Icon } from "@/components/ui/icon";
import { IconAvatar } from "@/components/ui/icon-avatar";
import { Badge } from "@/components/ui/badge";
import { useConnectorLogo } from "@/features/jobs/hooks/use-connector-logo";
import { JobSyncChip } from "@/features/ats-sync/components/job-sync-chip";
import { useAuth } from "@/features/auth";
import { useStartTalentSearchFromJob } from "@/hooks/use-start-talent-search-from-job";

interface JobDetailHeaderProps {
  job: Job;
  jobIndex?: number;
  totalJobs?: number;
  onPrevJob?: () => void;
  onNextJob?: () => void;
  onOpenSettings: () => void;
  onOpenImport: () => void;
  onStatusChange?: (action: "close" | "reopen" | "archive") => void;
}

function Dot() {
  return <div className="size-1 rounded-full bg-contrast/30 shrink-0" />;
}

function MetaBadge({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-contrast/4 border border-contrast/5 inline-flex items-center justify-center overflow-hidden rounded-md p-0.5 shrink-0">
      {children}
    </div>
  );
}

function AtsMetaContent({ link }: { link?: JobAtsLinkSummary | null }) {
  const logoSrc = useConnectorLogo(link?.connector_id);
  return (
    <>
      <div className="flex items-center px-0.5">
        <div className="size-[13px] rounded-[3px] bg-contrast/10 shrink-0 flex items-center justify-center overflow-hidden">
          {logoSrc ? (
            <Image
              src={logoSrc}
              alt=""
              width={13}
              height={13}
              className="size-full object-cover"
            />
          ) : (
            <Icon
              name="IconSquareArrowTopRight2"
              size={9}
              className="text-heading opacity-50"
            />
          )}
        </div>
      </div>
      <div className="flex items-center pl-0.5 pr-0.5">
        <span className="text-xs font-medium text-contrast/75 leading-4 whitespace-nowrap">
          {link?.connector_name ?? "No External ATS"}
        </span>
      </div>
    </>
  );
}

export function JobDetailHeader({
  job,
  jobIndex = 0,
  totalJobs = 1,
  onPrevJob,
  onNextJob,
  onOpenSettings,
  onOpenImport,
  onStatusChange,
}: JobDetailHeaderProps) {
  const isActive = job.status === "active";
  const createdDate = formatShortDate(job.created_at);
  const { selectedProjectId } = useAuth();
  const projectId = job.project_id ?? selectedProjectId ?? null;
  const { start: startTalentSearch, isPending: isSearchTalentPending } =
    useStartTalentSearchFromJob(projectId);

  const handleSearchTalent = () => {
    void startTalentSearch({
      title: job.title,
      description: job.description,
    });
  };
  return (
    <>
      <div
        className="border-b border-border px-4 py-4 md:hidden"
        data-testid="job-detail-header-mobile"
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex items-center gap-2">
                <h1 className="text-base font-medium text-heading leading-6 break-words">
                  {job.title}
                </h1>
                {isActive && (
                  <span
                    role="status"
                    aria-label="Active"
                    className="size-1.5 rounded-full bg-[#15803d] dark:bg-[#66dc7e] shrink-0"
                  />
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {!isActive && (
                  <Badge
                    variant="warning"
                    size="xs"
                    className="rounded-md"
                  >
                    Archived
                  </Badge>
                )}
                {/*
                 * No sibling-job pager here (ARBI-667): it clutters the mobile
                 * header and duplicates the tab strip's own navigation. The
                 * desktop header below keeps it.
                 */}
                <JobSyncChip jobId={job.id} />
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <LinkButton
                href="https://docs.neuroscale.ai/core/job-requisitions"
                target="_blank"
                rel="noopener noreferrer"
                variant="secondary"
                size="icon-sm"
                className="size-7 bg-transparent hover:bg-contrast/4"
                aria-label="Docs"
              >
                <div className="opacity-60">
                  <Icon
                    name="IconNewspaper1"
                    size={16}
                    className="text-heading"
                  />
                </div>
              </LinkButton>

              <JobActionsMenu
                job={job}
                onSearchTalent={handleSearchTalent}
                searchTalentDisabled={isSearchTalentPending}
                onClose={() => onStatusChange?.("close")}
                onReopen={() => onStatusChange?.("reopen")}
                onArchive={() => onStatusChange?.("archive")}
                onImport={onOpenImport}
                trigger={
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon-sm"
                    className="size-7 bg-transparent hover:bg-contrast/4"
                  >
                    <div className="opacity-60">
                      <Icon
                        name="IconDotGrid1x3Vertical"
                        size={16}
                        className="text-heading"
                      />
                    </div>
                  </Button>
                }
              />

              <Button
                type="button"
                onClick={onOpenSettings}
                variant="secondary"
                size="icon-sm"
                className="size-7 bg-transparent hover:bg-contrast/4"
              >
                <div className="opacity-60">
                  <Icon
                    name="IconSettingsGear2"
                    size={16}
                    className="text-heading"
                  />
                </div>
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {job.job_manager && (
              <MetaBadge>
                <div className="flex items-center px-0.5">
                  <Avatar className="size-[13px]">
                    <AvatarImage src={job.job_manager.avatar_url} alt="" />
                    <AvatarFallback>
                      <Icon
                        name="IconPeopleCircle"
                        size={8}
                        className="text-heading opacity-20"
                      />
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex items-center pl-0.5 pr-0.5">
                  <span className="max-w-[180px] truncate text-xs font-medium text-contrast/75 leading-4">
                    {[job.job_manager.first_name, job.job_manager.last_name]
                      .filter(Boolean)
                      .join(" ") || job.job_manager.email}
                  </span>
                </div>
              </MetaBadge>
            )}

            <MetaBadge>
              <AtsMetaContent link={job.ats_link} />
            </MetaBadge>

            <MetaBadge>
              <div className="flex items-center px-0.5">
                <div className="size-[13px] rounded-[3px] bg-contrast/10 shrink-0 flex items-center justify-center">
                  <Icon
                    name="IconPeopleCircle"
                    size={9}
                    className="text-heading opacity-50"
                  />
                </div>
              </div>
              <div className="flex items-center pl-0.5 pr-0.5">
                <span className="text-xs font-medium text-contrast/75 leading-4 whitespace-nowrap">
                  {job.candidate_count} Profiles
                </span>
              </div>
            </MetaBadge>
          </div>
        </div>
      </div>

      <div
        className="hidden border-b border-border px-4 py-9 md:flex md:items-center md:gap-3 md:shrink-0"
        data-testid="job-detail-header-desktop"
      >
        <div className="flex-1 min-w-0 flex flex-col gap-2.5">
          <div className="flex items-center gap-[18px]">
            <h1 className="text-lg font-medium text-heading whitespace-nowrap">
              {job.title}
            </h1>

            <Badge
              variant={isActive ? "success" : "warning"}
              size="xs"
              className="rounded-md"
            >
              {isActive ? "Active" : "Archived"}
            </Badge>

            <JobSyncChip jobId={job.id} />

            <div className="w-px h-4 bg-contrast/10 shrink-0" />

            {totalJobs > 0 && (
              <div className="flex items-center gap-2.5 shrink-0">
                {totalJobs > 1 && (
                  <>
                    <button
                      type="button"
                      disabled={jobIndex <= 0}
                      onClick={onPrevJob}
                      className="size-5 flex items-center justify-center bg-contrast/5 rounded cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed hover:bg-contrast/8 transition-colors"
                    >
                      <Icon
                        name="IconChevronLeft"
                        size={11}
                        className="text-heading opacity-50"
                      />
                    </button>
                    <button
                      type="button"
                      disabled={jobIndex >= totalJobs - 1}
                      onClick={onNextJob}
                      className="size-5 flex items-center justify-center bg-contrast/5 rounded cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed hover:bg-contrast/8 transition-colors"
                    >
                      <Icon
                        name="IconChevronRight"
                        size={11}
                        className="text-heading opacity-50"
                      />
                    </button>
                  </>
                )}
                <span className="text-sm text-contrast/40 whitespace-nowrap">
                  {jobIndex + 1} of {totalJobs}{" "}
                  {totalJobs === 1 ? "job" : "jobs"}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 h-5">
            <MetaBadge>
              <div className="flex items-center px-0.5">
                <IconAvatar
                  src={job.employer.cover_url}
                  size={13}
                  alt={job.employer.name}
                  className="rounded-[3px]"
                  icon={
                    <Icon name="IconBag" className="text-heading opacity-50" />
                  }
                />
              </div>
              <div className="flex items-center pl-0.5 pr-0.5">
                <span className="text-xs font-medium text-contrast/75 leading-4 whitespace-nowrap">
                  {job.employer.name}
                </span>
              </div>
            </MetaBadge>

            <Dot />

            {job.job_manager && (
              <>
                <MetaBadge>
                  <div className="flex items-center px-0.5">
                    <Avatar className="size-[13px]">
                      <AvatarImage src={job.job_manager.avatar_url} alt="" />
                      <AvatarFallback>
                        <Icon
                          name="IconPeopleCircle"
                          size={8}
                          className="text-heading opacity-20"
                        />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="flex items-center pl-0.5 pr-0.5">
                    <span className="text-xs font-medium text-contrast/75 leading-4 whitespace-nowrap">
                      {[job.job_manager.first_name, job.job_manager.last_name]
                        .filter(Boolean)
                        .join(" ") || job.job_manager.email}
                    </span>
                  </div>
                </MetaBadge>
                <Dot />
              </>
            )}

            <MetaBadge>
              <AtsMetaContent link={job.ats_link} />
            </MetaBadge>

            <Dot />

            <div className="flex items-center gap-1 shrink-0">
              <div className="opacity-70">
                <Icon name="IconCalendar1" size={13} className="text-heading" />
              </div>
              <span className="text-[13px] text-contrast/70 whitespace-nowrap">
                Created {createdDate}
              </span>
            </div>

            <Dot />

            <div className="flex items-center gap-1 shrink-0">
              <div className="opacity-70">
                <Icon
                  name="IconPeopleCircle"
                  size={13}
                  className="text-heading"
                />
              </div>
              <span className="text-[13px] text-contrast/70 whitespace-nowrap">
                {job.candidate_count} Profiles
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <JobActionsMenu
            job={job}
            onSearchTalent={handleSearchTalent}
            searchTalentDisabled={isSearchTalentPending}
            onClose={() => onStatusChange?.("close")}
            onReopen={() => onStatusChange?.("reopen")}
            onArchive={() => onStatusChange?.("archive")}
            trigger={
              <Button
                type="button"
                variant="secondary"
                size="icon-sm"
                className="size-7 bg-transparent hover:bg-contrast/4"
              >
                <div className="opacity-60">
                  <Icon
                    name="IconDotGrid1x3Vertical"
                    size={16}
                    className="text-heading"
                  />
                </div>
              </Button>
            }
          />

          <Button
            type="button"
            onClick={onOpenSettings}
            variant="secondary"
            size="icon-sm"
            className="size-7 bg-transparent hover:bg-contrast/4"
          >
            <div className="opacity-60">
              <Icon
                name="IconSettingsGear2"
                size={16}
                className="text-heading"
              />
            </div>
          </Button>

          <LinkButton
            href="https://docs.neuroscale.ai/core/job-requisitions"
            target="_blank"
            rel="noopener noreferrer"
            variant="secondary"
            size="sm"
            icon={<Icon name="IconNewspaper1" size={15} />}
          >
            <span className="text-shadow-[0px_0.5px_1px_rgba(0,0,0,0.15)]">
              Docs
            </span>
          </LinkButton>

          <Button
            type="button"
            onClick={onOpenImport}
            variant="primary"
            size="sm"
            icon={<Icon name="IconPeopleAdd" />}
          >
            Profile Import
          </Button>
        </div>
      </div>
    </>
  );
}
